import {v4 as uuidv4} from 'uuid'
import {useRouter} from 'next/router'
import {useEffect, useRef, useState} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Subscriptions} from '@elanco/gateway-utils'
import {sendScreenSetEventToDataLayer} from '@/utils/analytics'
import type {
	Block,
	Tersed,
	CampaignConfigContentITem,
} from '@/_new-code/services/kontent-ai/types'
import {
	logError,
	logMessage,
	logEvent,
} from '@/_new-code/services/logger/client'
import type {
	CampaignData,
	Consent as CampaignConsent,
	Preferences as CampaignPreferences,
} from '@/_new-code/models/campaign/campaign-data'
import {env} from '@/utils/env/client.mjs'
import type {CdcGigyaContentItem} from '../../auth/cdc-screensets/basic'
import {CdcGigyaBlock} from '../../auth/cdc-screensets/basic'
import type {
	OnSubmitEvent,
	OnBeforeValidationEvent,
	OnBeforeSubmitEvent,
} from '../../auth/cdc-screensets/models'
import {useAuth} from '../../auth/auth-provider'
import {setErrorLabel, FormSubmitErrorLabel} from './form-elements'
import {CDCScreenSetValidator} from './cdc-screenset-validator'
import {getBusinessData} from './business-search'
import {handleUTMQueryItem} from './cdc-screenset-helper'
import {CampaignInterestHandler} from './campaign-questions/campaign-interest-handler'
import {injectIntoScreenset} from './campaign-questions/campaign-question-injector'
import type {
	SingleSelectionContentItem,
	MultipleSelectionContentItem,
	DateSelectionContentItem,
} from './models'

export type CdcScreenSetWrapperContentItem = IContentItem<{
	wrappedScreenset: Elements.LinkedItemsElement<CdcGigyaContentItem>
	redirectPathOverride: Elements.TextElement
	campaignQuestions: Elements.LinkedItemsElement<
		| SingleSelectionContentItem
		| MultipleSelectionContentItem
		| DateSelectionContentItem
	>
	tabTitle: Elements.TextElement
	bsNoResultText: Elements.TextElement
	formSubmissionGenericErrorMessage: Elements.TextElement
	customerLeavePageMessage: Elements.TextElement
	disableFormScrollingAfterSubmit: Elements.MultipleChoiceElement
}>

export const CdcScreenSetWrapperBlock: Block<
	CdcScreenSetWrapperContentItem
> = ({
	block: {
		elements: {
			bsNoResultText,
			campaignQuestions,
			redirectPathOverride,
			formSubmissionGenericErrorMessage,
			customerLeavePageMessage, // This message will be visible if the user attempts to navigate away from the form whilst the form is submitting
			disableFormScrollingAfterSubmit,
			wrappedScreenset: [wrappedScreenset],
		},
		system: {id, type},
	},
	page,
	...context
}) => {
	const {userDetails} = useAuth()
	const userDetailsRef = useRef(userDetails)
	const router = useRouter()
	const {events, query, locale = ''} = router
	const screensetValidator = new CDCScreenSetValidator()
	const beforeUnloadEventName = 'beforeunload'
	const beforeRouteUnloadEventName = 'routeChangeStart'
	const disableFormScrolling =
		disableFormScrollingAfterSubmit[0]?.codename === 'yes'
	const campaignInterestHandler = new CampaignInterestHandler()
	const containsCampaignQuestions = campaignQuestions.length !== 0
	const cdcScreenSetHandlerRef = useRef(null as HTMLDivElement | null)
	const holdRedirectUntilInterestApiFinished = campaignQuestions.length !== 0
	const [attachBeforeUnloadEvent, setAttachBeforeUnloadEvent] = useState<
		boolean | null
	>(null)
	const [campaignInterestSuccess, setCampaignInterestSuccess] = useState<
		boolean | null
	>(null)
	const displayUnloadEventAlert = useRef(false) // using to prevent double call of routeChangeStart event

	const handleBeforeUnloadEvent = (e: BeforeUnloadEvent): boolean => {
		e.preventDefault()
		return true
	}

	const handleRouteChangeEvent = (): void => {
		if (attachBeforeUnloadEvent) {
			if (displayUnloadEventAlert.current) {
				displayUnloadEventAlert.current = false
				// eslint-disable-next-line no-alert -- confirmation to user is required
				if (!confirm(customerLeavePageMessage)) {
					logError(
						new Error(
							'Route change Aborted by the user via confirmation dialog for campaign form'
						)
					)
					events.emit('routeChangeError')
					throw new Error('Route change aborted by user') // required to prevent navigation
				}
			} else {
				logError(
					new Error(
						'Route change Aborted :unload event alert is not true for campaign form'
					)
				)
				events.emit('routeChangeError')
				throw new Error('Route change aborted by user') // required to prevent navigation
			}
		}
	}

	useEffect(() => {
		userDetailsRef.current = userDetails
	}, [userDetails])

	useEffect(() => {
		if (attachBeforeUnloadEvent) {
			displayUnloadEventAlert.current = true
			window.addEventListener(
				beforeUnloadEventName,
				handleBeforeUnloadEvent
			)
			events.on(beforeRouteUnloadEventName, handleRouteChangeEvent)

			return () => {
				window.removeEventListener(
					beforeUnloadEventName,
					handleBeforeUnloadEvent
				)
				events.off(beforeRouteUnloadEventName, handleRouteChangeEvent)
			}
		}
		window.removeEventListener(
			beforeUnloadEventName,
			handleBeforeUnloadEvent
		)
		events.off(beforeRouteUnloadEventName, handleRouteChangeEvent)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- This block have single dependency on attachBeforeUnloadEvent
	}, [attachBeforeUnloadEvent])

	useEffect(() => {
		if (campaignInterestSuccess !== null) {
			if (!campaignInterestSuccess) {
				logError(
					new Error(
						`Campaign Form submission error${formSubmissionGenericErrorMessage}`
					)
				)
				setErrorLabel(
					FormSubmitErrorLabel(),
					formSubmissionGenericErrorMessage
				)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- This block have single dependency on campaignInterestSuccess
	}, [campaignInterestSuccess])

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- Implicit is better here
	const onSubmit = (event: OnSubmitEvent) => {
		const businessSearchData = getBusinessData()
		const business = businessSearchData ?? event.formModel.data.business

		const data = {
			...event.formModel.data,
			business,
		}
		logMessage('Successfully fetched business data for campaign form.')
		// Injected elements must be removed from profile object or CDC will error
		const injectedElements = campaignInterestHandler.getInjectedElements()
		logMessage('fetched injected elements for removal for campaign form')
		const profile = containsCampaignQuestions
			? Object.fromEntries(
					Object.entries(event.formModel.profile).filter(
						([key, _value]) => {
							return !injectedElements.includes(key)
						}
					)
				)
			: event.formModel.profile

		return {...event.formModel, profile, data}
	}

	const onBeforeValidation = (_event: OnBeforeValidationEvent): void => {
		if (containsCampaignQuestions) {
			campaignInterestHandler.validate()
		}

		screensetValidator.validate()
	}

	const onAfterScreenLoad = (_event: OnAfterScreenLoadEvent): void => {
		screensetValidator.setup(locale, bsNoResultText)
		if (containsCampaignQuestions) {
			injectIntoScreenset(id, campaignQuestions, campaignInterestHandler)
		}
	}

	const onBeforeSubmit = (_event: OnBeforeSubmitEvent): boolean => {
		if (containsCampaignQuestions && !campaignInterestHandler.isValid()) {
			logMessage('Campaign interest is not valid')
			return false
		}
		const role = _event.formData['data.role'] as string
		const farmerRoles = ['Beef Farmer', 'Sheep Farmer', 'Dairy Farmer']
		if (role && farmerRoles.includes(role)) {
			return screensetValidator.isValid(false)
		}
		return screensetValidator.isValid()
	}

	const onAfterSubmit = async (
		event: OnAfterSubmitEvent & {subscriptions: Subscriptions}
	): Promise<boolean> => {
		logMessage('Campaign Form Submitted Successfully')

		const cdcScreenSetHandlerElement = cdcScreenSetHandlerRef.current
		if (!disableFormScrolling && cdcScreenSetHandlerElement) {
			const {top} = cdcScreenSetHandlerElement.getBoundingClientRect()
			window.scrollTo({
				top: window.scrollY + top - 270,
				behavior: 'smooth',
			})
		}
		const {UID} = event.response
		const {response, form} = event
		const medium = handleUTMQueryItem(query.utm_medium) ?? ''
		const source = handleUTMQueryItem(query.utm_source) ?? ''
		const pageConfig = page.elements.config[0]
		let campaignSubmissionResponse = true

		const campaignId =
			pageConfig && 'campaignId' in pageConfig.elements
				? (pageConfig as Tersed<CampaignConfigContentITem>).elements
						.campaignId
				: null

		const blockCodename = type

		if (campaignId) {
			setAttachBeforeUnloadEvent(true)
			logEvent({
				name: 'ATTACH_BEFORE_UNLOAD_EVENT_ENABLED_FOR_CAMPAIGN_FORM',
				properties: {
					campaignId,
				},
			})

			const PREFERENCE_MAPPING: Record<string, string> = {
				ProductServiceCommunications: 'productService',
				businessManagement: 'businessManagement',
				professionalEducation: 'professionalEducation',
				ProductPromotions: 'productPromotions',
			}

			const flatPreferences = Object.keys(
				event.subscriptions.preference?.b2b ?? {}
			).reduce<CampaignPreferences>(
				(acc, key) => {
					const mappedKey = PREFERENCE_MAPPING[key]
					if (mappedKey) {
						acc[mappedKey] =
							(event.subscriptions.preference?.b2b?.[key]?.email
								.isSubscribed as unknown as string) === 'true'
					}
					return acc
				},
				{
					productService: false,
					businessManagement: false,
					professionalEducation: false,
					productPromotions: false,
				}
			)

			const flatConsent = Object.keys(
				event.preferences
			).reduce<CampaignConsent>(
				(acc, key) => {
					acc[key] =
						(event.preferences[key as keyof Preferences]
							?.isConsentGranted as unknown as string) === 'true'
					return acc
				},
				{email: false, sms: false}
			)

			const rawBusiness = ((
				event.response.requestParams as {
					data: Record<string, unknown>
					[key: string]: unknown
				}
			).data.business ?? {}) as Record<string, unknown>

			// Convert all business values to string or undefined
			const business: Record<string, string | undefined> =
				Object.fromEntries(
					Object.entries(rawBusiness).map(([key, value]) => [
						key,
						value === null || value === undefined
							? undefined
							: String(value),
					])
				)

			const data: CampaignData = {
				submissionId: uuidv4(),
				submissionTimeStamp: new Date().toISOString(),
				formId: campaignId,
				sourceLocale: locale,
				sourcePortal: env.NEXT_PUBLIC_TOWER,
				// The slash is needed between the origin and the locale but not between the locale and the path
				sourcePage: `${window.location.origin}/${router.locale}${router.asPath}`,
				/**
				 * Data gets added in CampaignInterestHandler.send() method
				 * because its injected into the CDC Screen-Set
				 */
				formResponse: [],
				formCategory: 'campaign',
				cdcProfile: {
					UID: userDetailsRef.current.id ?? String(UID),
					email: event.profile.email,
					salutation: event.data.salutation,
					role: event.data.role,
					role2: event.data.role2,
					professionalNumberJurisdiction:
						event.data.professionalNumberJurisdiction,
					professionalNumber: event.data.professionalNumber,
					firstName: event.profile.firstName,
					lastName: event.profile.lastName,
					phone: event.profile.phones?.[0]?.number,
					preferences: flatPreferences,
					consent: flatConsent,
					business,
				},
			}

			campaignSubmissionResponse =
				await campaignInterestHandler.send(data)
			setCampaignInterestSuccess(campaignSubmissionResponse)
			logEvent({
				name: 'CAMPAIGN_INTEREST_DATA_SENT',
				properties: {
					campaignId,
					contactId: String(UID),
					medium,
					source,
				},
			})
			setAttachBeforeUnloadEvent(false)

			if (form === 'gigya-subscribe-with-email-form') {
				sendScreenSetEventToDataLayer(
					response,
					form,
					blockCodename,
					campaignId
				)
			}
		}

		if (form === 'gigya-subscribe-with-email-form' && !campaignId) {
			sendScreenSetEventToDataLayer(response, form, blockCodename)
		}

		logEvent({
			name: 'CAMPAIGN_SUBMISSION_COMPLETED',
			properties: {
				campaignSubmissionResponse,
				formName: form,
			},
		})
		return campaignSubmissionResponse
	}

	const eventListeners = {
		onSubmit,
		onBeforeValidation,
		onAfterScreenLoad,
		onBeforeSubmit,
		onAfterSubmit,
	}

	const redirectPath = redirectPathOverride
		? redirectPathOverride
		: wrappedScreenset?.elements.redirectPath

	if (wrappedScreenset) {
		wrappedScreenset.elements.redirectPath = redirectPath ?? ''
	}

	return (
		<div ref={cdcScreenSetHandlerRef}>
			{wrappedScreenset ? (
				<>
					<CdcGigyaBlock
						block={wrappedScreenset}
						campaignInterestSuccess={campaignInterestSuccess}
						eventRegistration={eventListeners}
						holdRedirectUntilInterestApiFinished={
							holdRedirectUntilInterestApiFinished
						}
						page={page}
						{...context}
					/>
					<span className="campaign-form-submit-error-message block text-center text-sm font-bold leading-5 text-red-500" />
				</>
			) : null}
		</div>
	)
}
